// src/pages/Home.jsx
import React from "react";
import { Link } from "react-router-dom";

const Home = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-indigo-50">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* soft background glows */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-sky-400/10 blur-3xl" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-14 pb-16 md:pt-24 md:pb-24">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                GrowMax-OGCS
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-600" />
                Training • Consulting • Growth
              </span>

              <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Home Page
                <span className="block text-indigo-600">Welcome to GrowMax</span>
              </h1>

              <p className="mt-4 max-w-xl text-gray-600">
                Practical programs, expert-led sessions, and hands-on guidance for
                sales, leadership, digital, and construction excellence.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/programs"
                  className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 active:scale-[0.99]"
                >
                  Explore Programs
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-900 shadow-sm transition hover:bg-gray-50"
                >
                  Contact Us
                </Link>
              </div>

              {/* trust bar */}
              <div className="mt-6 flex flex-wrap items-center gap-6 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                  Pan-India Sessions
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-indigo-500" />
                  Industry Experts
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                  Outcome Focused
                </div>
              </div>
            </div>

            {/* illustration card */}
            <div className="relative mx-auto w-full">
              <div className="mx-auto aspect-[5/4] w-full max-w-lg rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex h-full items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-sky-50">
                  <div className="text-center">
                    <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-indigo-600/10 ring-1 ring-indigo-600/20" />
                    <p className="text-sm font-medium text-gray-900">GrowMax Advantage</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Real-world case studies • Actionable templates • Mentorship
                    </p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-3 rounded-2xl bg-white px-4 py-3 text-xs font-semibold text-gray-800 shadow-lg ring-1 ring-gray-100">
                ⭐ 4.9/5 from participants
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-5 md:grid-cols-3">
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition hover:shadow-md">
              <div className="mb-4 h-10 w-10 rounded-xl bg-indigo-600/10 ring-1 ring-indigo-600/20" />
              <h3 className="text-lg font-semibold text-gray-900">Sales & Presentation</h3>
              <p className="mt-1 text-sm text-gray-600">
                Pitching, negotiation, CRM, and closing techniques with role-plays.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition hover:shadow-md">
              <div className="mb-4 h-10 w-10 rounded-xl bg-indigo-600/10 ring-1 ring-indigo-600/20" />
              <h3 className="text-lg font-semibold text-gray-900">Digital & Analytics</h3>
              <p className="mt-1 text-sm text-gray-600">
                SEO/SEM, content, and simple dashboards to measure ROI.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition hover:shadow-md">
              <div className="mb-4 h-10 w-10 rounded-xl bg-indigo-600/10 ring-1 ring-indigo-600/20" />
              <h3 className="text-lg font-semibold text-gray-900">Construction Excellence</h3>
              <p className="mt-1 text-sm text-gray-600">
                Tailored programs for contractors, PMs, and engineering teams.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="pb-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid overflow-hidden rounded-2xl border border-gray-100 bg-white text-center shadow-sm md:grid-cols-3">
            <div className="p-6">
              <div className="text-3xl font-bold text-gray-900">18+</div>
              <div className="text-sm text-gray-500">Hours / Complete Program</div>
            </div>
            <div className="p-6 border-t border-gray-100 md:border-t-0 md:border-l">
              <div className="text-3xl font-bold text-gray-900">31+ yrs</div>
              <div className="text-sm text-gray-500">Industry Experience</div>
            </div>
            <div className="p-6 border-t border-gray-100 md:border-t-0 md:border-l">
              <div className="text-3xl font-bold text-gray-900">Pan-India</div>
              <div className="text-sm text-gray-500">Corporate & On-site Training</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 via-indigo-500 to-sky-500 p-6 md:p-10">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <div className="relative">
              <h2 className="text-2xl font-bold text-white md:text-3xl">
                Ready to transform your team’s performance?
              </h2>
              <p className="mt-2 max-w-2xl text-indigo-50">
                Talk to us about a custom training plan aligned to your KPIs.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-gray-900 shadow-sm transition hover:bg-gray-100"
                >
                  Contact Us
                </Link>
                <Link
                  to="/booking"
                  className="inline-flex items-center justify-center rounded-xl border border-white/30 bg-transparent px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-white/10"
                >
                  Book a Session
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
